import { useCallback, useEffect, useState } from 'react';
import { getHostedToken, completeAuthorizeNet } from 'services/authorizeNet/authorizeNet';
import { resolveRecurrence } from 'services/payment/rules';
import { REACT_APP_RULE_OMIT_PAYMENT_COUNTRY_EXCEPTION } from 'config';
import {
  AuthnetAction,
  AuthnetCompleteRequest,
  AuthnetMessagePayload,
  AuthnetResponseCode,
} from 'services/authorizeNet/types';

interface UseAuthorizeNetHandlerOptions {
  objectData: any;
  region: string;
  sessionToken: string;
  onError: (type: string) => void;
  setStatus: (status: string) => void;
  setSuccess: (success: boolean) => void;
}

export const APPROVED_RESPONSE_CODE: AuthnetResponseCode = '1';

export function parseAuthnetMessage(data: unknown): AuthnetMessagePayload | null {
  if (typeof data !== 'string' || data.length === 0) {
    return null;
  }

  const params = new URLSearchParams(data);
  const action = params.get('action') as AuthnetAction | null;
  if (!action) {
    return null;
  }

  if (action !== 'transactResponse') {
    return { action };
  }

  const raw = params.get('response');
  if (!raw) {
    return { action };
  }

  try {
    const parsed = JSON.parse(raw);
    return {
      action,
      responseCode: String(parsed.responseCode) as AuthnetResponseCode,
      transId: typeof parsed.transId === 'string' ? parsed.transId : undefined,
    };
  } catch {
    return { action };
  }
}

function buildCompleteRequest(objectData: any, transId: string, region: string): AuthnetCompleteRequest {
  return {
    transId,
    subscription: {
      id_quotation: objectData.idQuotation,
      id_application: objectData.idApplication,
      recurrent: resolveRecurrence({
        market: region,
        country: objectData.country || '',
        frequency: objectData.frequency,
        automaticApproval: objectData.automaticApproval,
        userAuthorized: Boolean(objectData.recurrent),
        countryException: REACT_APP_RULE_OMIT_PAYMENT_COUNTRY_EXCEPTION,
        isFirstPaymentScreen: true,
      }).recurrent,
      paymaster: objectData.payingForPolicy,
      paymaster_relationship:
        objectData.payingForPolicy === 'Other' ? objectData.relationship : objectData.payingForPolicy,
    },
    payer: {
      first_name: objectData.firstName,
      last_name: objectData.lastName,
      middle_name: objectData.middleName,
      address1: objectData.address,
      address2: '',
      city: objectData.cityForFlywire || objectData.city,
      state: objectData.state,
      zip: objectData.zipCode,
      country: objectData.country,
      phone: objectData.phone,
      email: objectData.email,
      paymaster: objectData.payingForPolicy,
      paymaster_relationship: objectData.relationship,
    },
  };
}

export function useAuthorizeNetHandler({
  objectData,
  region,
  sessionToken,
  onError,
  setStatus,
  setSuccess,
}: UseAuthorizeNetHandlerOptions) {
  const [formToken, setFormToken] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);

  const handleResult = useCallback(
    async (payload: AuthnetMessagePayload) => {
      if (payload.action === 'cancel') {
        setFormToken('');
        setIsLoaded(false);
        return;
      }

      if (payload.action !== 'transactResponse') {
        return;
      }

      if (payload.responseCode !== APPROVED_RESPONSE_CODE || !payload.transId) {
        setFormToken('');
        setIsLoaded(false);
        onError('payment');
        return;
      }

      setFormToken('');
      setIsLoaded(true);
      const res = await completeAuthorizeNet(buildCompleteRequest(objectData, payload.transId, region), sessionToken, region);
      setIsLoaded(false);

      if (!res.success) {
        onError('payment');
        return;
      }

      setSuccess(true);
      setStatus('success');
    },
    [objectData, region, sessionToken, onError, setStatus, setSuccess],
  );

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      const payload = parseAuthnetMessage(event.data);
      if (payload) {
        handleResult(payload);
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleResult]);

  const startAuthorizeNet = useCallback(async () => {
    setIsLoaded(true);
    const res = await getHostedToken(sessionToken, region);
    setIsLoaded(false);

    if (!res.success) {
      onError('payment');
      return;
    }

    setFormToken(res.data.formToken);
  }, [sessionToken, region, onError]);

  const closeAuthorizeNet = useCallback(() => {
    setFormToken('');
    setIsLoaded(false);
  }, []);

  return { formToken, isLoaded, startAuthorizeNet, closeAuthorizeNet };
}
